// Transaction Wizard — the client half of the model (docs/plans/transaction-wizard.md
// §5): the normaliser (a mirror of `twasta_runtime_engine/wizard/model.py`),
// targets, the value references (`{header.X}`, `{line.X}`, `{lines.X}`, …) and
// the expression bridge onto the ONE platform expression language.
//
// Pure: no React, no transport. machine.ts / ops.ts / the step components all
// read values through here, so a token means the same thing everywhere.

import { evaluate, truthy, Vector, ExprError, SAFE_FUNCS, DATE_FUNCS } from '../expr'
import type { ColumnDef, SqlModel, TransactionModel } from '../txn/types'
import { allSqlModels, childFormsOf, columnFormKey, columnDbName, headerFormNo } from '../txn/utils'
import type {
  WizardFrame, WizardFrameValues, WizardModel, WizardNext, WizardStep, WizardStepKind,
} from './contract'
import { WIZARD_STEP_KINDS } from './contract'

export type Row = Record<string, unknown>

// ── Normaliser ───────────────────────────────────────────────────────────

export const DEFAULT_LIMITS = { max_loop_iterations: 500, max_call_depth: 3 }

/** Accepts the file shape (`{"wizard": {...}}`) or the inner dict and returns
 *  the inner model with the defaults the server's normaliser fills. A model
 *  the server already normalised passes through unchanged. */
export function normalizeWizard(raw: unknown): WizardModel {
  const outer = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {}
  const inner = (outer.wizard && typeof outer.wizard === 'object')
    ? outer.wizard as Record<string, unknown> : outer
  const steps = (Array.isArray(inner.steps) ? inner.steps : [])
    .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
    .map((s, i) => ({
      ...s,
      id: String(s.id ?? `step_${i + 1}`).trim(),
      kind: (WIZARD_STEP_KINDS as readonly string[]).includes(String(s.kind))
        ? s.kind as WizardStepKind : 'info',
    })) as unknown as WizardStep[]
  const mode = inner.mode === 'edit' || inner.mode === 'add_or_edit' || inner.mode === 'fill' ? inner.mode : 'add'
  const limits = (inner.limits && typeof inner.limits === 'object') ? inner.limits as Record<string, unknown> : {}
  const positive = (v: unknown, d: number) => {
    const n = Number(v)
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : d
  }
  return {
    ...(inner as unknown as WizardModel),
    obj_name: String(inner.obj_name ?? ''),
    base_object: String(inner.base_object ?? ''),
    mode,
    inputs: Array.isArray(inner.inputs) ? inner.inputs as WizardModel['inputs'] : [],
    vars: Array.isArray(inner.vars) ? inner.vars as WizardModel['vars'] : [],
    tools: Array.isArray(inner.tools) ? inner.tools as WizardModel['tools'] : [],
    outputs: (inner.outputs && typeof inner.outputs === 'object') ? inner.outputs as Record<string, string> : {},
    steps,
    start: String(inner.start || steps[0]?.id || ''),
    suspend: inner.suspend === 'server' ? 'server' : 'memory',
    limits: {
      max_loop_iterations: positive(limits.max_loop_iterations, DEFAULT_LIMITS.max_loop_iterations),
      max_call_depth: positive(limits.max_call_depth, DEFAULT_LIMITS.max_call_depth),
    },
  }
}

export function stepById(model: WizardModel, id: string | null | undefined): WizardStep | null {
  if (!id) return null
  return model.steps.find(s => s.id === id) || null
}

/** Kinds that draw a screen of their own; the rest run and move on. */
const INTERACTIVE: ReadonlySet<WizardStepKind> = new Set<WizardStepKind>([
  'info', 'select', 'input', 'scan', 'grid', 'confirm', 'show', 'print', 'redirect',
  'sign', 'send',
])
export function isInteractive(kind: WizardStepKind): boolean {
  return INTERACTIVE.has(kind)
}

// ── Forms of the base transaction ────────────────────────────────────────

export interface FormInfo {
  formNo: string
  sm: SqlModel
  cols: ColumnDef[]
}

/** The base transaction's forms, indexed for name → column resolution. A
 *  wizard targets `header.qty` or `line.batch_no`; the record holds form keys
 *  (columnFormKey). Every lookup is case-insensitive over form key, db_name and
 *  the logical name, so an author may write any of the three. */
export interface FormIndex {
  headerNo: string
  lineForm: string
  forms: Record<string, FormInfo>
  /** form_no → child form_nos (direct). */
  children: Record<string, string[]>
}

export function buildFormIndex(base: TransactionModel | null | undefined, lineForm?: string): FormIndex {
  const forms: Record<string, FormInfo> = {}
  const children: Record<string, string[]> = {}
  if (!base || !Array.isArray(base.sql_models)) {
    return { headerNo: '1', lineForm: String(lineForm || '2'), forms, children }
  }
  const headerNo = headerFormNo(base)
  for (const { formNo, sm } of allSqlModels(base)) {
    forms[formNo] = { formNo, sm, cols: (sm.columns || []).map(w => w?.column).filter((c): c is ColumnDef => !!c) }
    children[formNo] = childFormsOf(base, formNo).map(c => c.formNo)
  }
  const firstChild = children[headerNo]?.[0]
  return { headerNo, lineForm: String(lineForm || firstChild || '2'), forms, children }
}

export function columnOf(fi: FormIndex, formNo: string, name: string): ColumnDef | null {
  const f = fi.forms[formNo]
  if (!f) return null
  const n = String(name || '').trim().replace(/\s+/g, '_').toLowerCase()
  if (!n) return null
  return f.cols.find(c => columnFormKey(c) === n)
    || f.cols.find(c => columnDbName(c) === n)
    || f.cols.find(c => String(c.name || '').toLowerCase() === n)
    || null
}

/** The record key a column is held under (its form key); an unknown name
 *  falls back to the lower-cased name, so a wizard over a model the client
 *  does not have still reads and writes consistently. */
export function keyOf(fi: FormIndex, formNo: string, name: string): string {
  const col = columnOf(fi, formNo, name)
  return col ? columnFormKey(col) : String(name || '').trim().toLowerCase()
}

export function headingOf(fi: FormIndex, formNo: string, name: string): string {
  const col = columnOf(fi, formNo, name)
  return String(col?.heading || col?.name || name)
}

/** Read one column from a row by any of its names. */
export function readCol(row: Row | null | undefined, fi: FormIndex, formNo: string, name: string): unknown {
  if (!row) return undefined
  const key = keyOf(fi, formNo, name)
  if (key in row) return row[key]
  if (name in row) return row[name]
  const lower = String(name).toLowerCase()
  for (const k of Object.keys(row)) if (k.toLowerCase() === lower) return row[k]
  const col = columnOf(fi, formNo, name)
  if (col) {
    const db = columnDbName(col)
    for (const k of Object.keys(row)) if (k.toLowerCase() === db) return row[k]
  }
  return undefined
}

export function isBlank(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '')
}

// ── Targets ──────────────────────────────────────────────────────────────

export interface Target {
  scope: 'header' | 'line' | 'var'
  /** Column (or var) name as written. */
  name: string
  /** The form the column belongs to (header form for 'header'). */
  formNo: string
}

/** `header.qty` · `line.qty` · `line3.qty` / `form3.qty` · `var.short`. A bare
 *  `qty` is a column of `defaultForm` (a grid's `editable`). */
export function parseTarget(t: string, fi: FormIndex, defaultForm?: string): Target | null {
  const raw = String(t || '').trim()
  if (!raw) return null
  const dot = raw.indexOf('.')
  if (dot < 0) {
    const form = defaultForm || fi.lineForm
    return { scope: form === fi.headerNo ? 'header' : 'line', name: raw, formNo: form }
  }
  const root = raw.slice(0, dot).toLowerCase()
  const name = raw.slice(dot + 1)
  if (!name) return null
  if (root === 'header') return { scope: 'header', name, formNo: fi.headerNo }
  if (root === 'var') return { scope: 'var', name, formNo: '' }
  const m = /^(?:line|lines|form)(\d*)$/.exec(root)
  if (m) {
    const form = m[1] || defaultForm || fi.lineForm
    return { scope: form === fi.headerNo ? 'header' : 'line', name, formNo: form }
  }
  return null
}

export function targetLabel(t: Target, fi: FormIndex): string {
  if (t.scope === 'var') return t.name
  return headingOf(fi, t.formNo, t.name)
}

// ── Frame helpers ────────────────────────────────────────────────────────

export function liveRows(rows: Row[] | undefined): Row[] {
  return (rows || []).filter(r => !r._deleted)
}

/** The frame's current line on `formNo` (null when none is current). */
export function currentLine(frame: WizardFrame, formNo: string): Row | null {
  if (frame.line_index == null) return null
  if ((frame.line_form || '') !== formNo) return null
  const row = frame.record.details[formNo]?.[frame.line_index]
  return row && !row._deleted ? row : null
}

// ── Value references ─────────────────────────────────────────────────────

export interface WizardScope {
  fi: FormIndex
  header: Row
  details: Record<string, Row[]>
  /** The current line (of `lineForm`), or null. */
  line: Row | null
  lineForm: string
  vars: Row
  input: Row
  /** Values a step captured: `{step.ID.value}`. */
  steps: Record<string, unknown>
  scan?: Row
  result?: Row
  run: Row
  call?: { outcome?: string; outputs?: Row }
  user?: { id?: string; name?: string }
}

/** The scope for one frame at one step (`step.form_no` overrides `line_form`). */
export function scopeOf(
  frame: WizardFrame, fi: FormIndex, opts: {
    step?: WizardStep | null
    run?: Row
    extra?: Partial<WizardScope>
    user?: { id?: string; name?: string }
  } = {},
): WizardScope {
  const lineForm = String(opts.step?.form_no || frame.line_form || fi.lineForm)
  return {
    fi,
    header: frame.record.header || {},
    details: frame.record.details || {},
    line: currentLine(frame, lineForm),
    lineForm,
    vars: frame.vars || {},
    input: frame.input || {},
    steps: frame.steps || {},
    run: {
      txn_id: frame.txn_id ?? '',
      mode: frame.mode,
      save_status: frame.save_status || 'none',
      ...(opts.run || {}),
    },
    user: opts.user,
    ...(opts.extra || {}),
  }
}

/** The values a server-side wizard op may read (`wizard_scan` / `wizard_fetch`). */
export function frameValues(scope: WizardScope): WizardFrameValues {
  const lines: Record<string, Row[]> = {}
  for (const [fno, rows] of Object.entries(scope.details)) {
    lines[fno] = liveRows(rows).map(stripControl)
  }
  return {
    header: stripControl(scope.header),
    line: scope.line ? stripControl(scope.line) : null,
    lines,
    vars: scope.vars,
    input: scope.input,
    run: scope.run,
  }
}

export function stripControl(row: Row): Row {
  const out: Row = {}
  for (const [k, v] of Object.entries(row || {})) {
    if (k === '__children' || k.startsWith('_')) continue
    out[k] = v
  }
  return out
}

const ROOT_RE = /^(header|line\d*|lines\d*|form\d+|var|input|step|scan|result|run|call|user|today|now)$/i

/** `{path}` or `{path|default}`. Only paths whose root is a wizard root are
 *  tokens — any other brace text is left as written. */
const TOKEN_RE = /\{\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_-]+)*)\s*(?:\|([^{}]*))?\}/g

function isoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function getPath(obj: unknown, parts: string[]): unknown {
  let cur: unknown = obj
  for (const p of parts) {
    if (cur === null || cur === undefined || typeof cur !== 'object') return undefined
    const rec = cur as Record<string, unknown>
    if (p in rec) { cur = rec[p]; continue }
    const lower = p.toLowerCase()
    const k = Object.keys(rec).find(x => x.toLowerCase() === lower)
    cur = k === undefined ? undefined : rec[k]
  }
  return cur
}

/** Resolve one token path. `known` is false for a path that is not a wizard
 *  token at all (left as literal text by a template). */
export function resolvePath(path: string, scope: WizardScope): { known: boolean; value: unknown } {
  const parts = path.split('.')
  const root = parts[0].toLowerCase()
  if (!ROOT_RE.test(root)) return { known: false, value: undefined }
  const rest = parts.slice(1)
  const { fi } = scope
  switch (root) {
    case 'today': return { known: true, value: isoDate(new Date()) }
    case 'now': return { known: true, value: new Date().toISOString() }
    case 'header':
      return { known: true, value: rest.length ? readCol(scope.header, fi, fi.headerNo, rest.join('.')) : undefined }
    case 'var': return { known: true, value: getPath(scope.vars, rest) }
    case 'input': return { known: true, value: getPath(scope.input, rest) }
    case 'scan': return { known: true, value: getPath(scope.scan || {}, rest) }
    case 'result': return { known: true, value: getPath(scope.result || {}, rest) }
    case 'run': return { known: true, value: getPath(scope.run, rest) }
    case 'call': return { known: true, value: getPath(scope.call || {}, rest) }
    case 'user': return { known: true, value: getPath(scope.user || {}, rest) }
    case 'step': {
      // {step.ID.value} — `value` is the only property a step exposes today;
      // {step.ID} reads the same.
      const id = rest[0]
      const v = id === undefined ? undefined : scope.steps[id]
      const sub = rest.slice(1).filter(p => p !== 'value')
      return { known: true, value: sub.length ? getPath(v, sub) : v }
    }
    default: break
  }
  const m = /^(line|lines|form)(\d*)$/.exec(root)
  if (!m || !rest.length) return { known: true, value: undefined }
  const col = rest.join('.')
  const formNo = m[2] || scope.lineForm
  if (m[1] === 'lines') {
    if (formNo === fi.headerNo) return { known: true, value: Vector.of2([readCol(scope.header, fi, formNo, col)]) }
    const rows = liveRows(scope.details[formNo])
    return { known: true, value: Vector.of2(rows.map(r => readCol(r, fi, formNo, col))) }
  }
  if (formNo === fi.headerNo) return { known: true, value: readCol(scope.header, fi, formNo, col) }
  // {line.X} / {formN.X}: the current line when it is on that form.
  const line = formNo === scope.lineForm ? scope.line : null
  return { known: true, value: line ? readCol(line, fi, formNo, col) : undefined }
}

function parseDefault(raw: string): unknown {
  const t = raw.trim()
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t)
  if (t === 'true' || t === 'True') return true
  if (t === 'false' || t === 'False') return false
  const q = /^(['"])(.*)\1$/.exec(t)
  return q ? q[2] : t
}

export interface Rendered {
  value: unknown
  /** Tokens that resolved blank with no `|default` (the `open` action's rule:
   *  an unresolved token in a `set` skips it, in a `match` refuses it). */
  missing: string[]
}

/** Render one template. A template that is exactly one token keeps the raw
 *  type; mixed text renders as a string (unresolved tokens → ''). Non-strings
 *  pass through; arrays / plain objects render member by member. */
export function renderTemplate(value: unknown, scope: WizardScope): Rendered {
  const missing: string[] = []
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') return renderString(v, scope, missing)
    if (Array.isArray(v)) return v.map(walk)
    if (v && typeof v === 'object' && !(v instanceof Vector)) {
      const out: Record<string, unknown> = {}
      for (const [k, x] of Object.entries(v)) out[k] = walk(x)
      return out
    }
    return v
  }
  return { value: walk(value), missing }
}

/** Convenience: the rendered value only. */
export function render(value: unknown, scope: WizardScope): unknown {
  return renderTemplate(value, scope).value
}

/** A value as an expression or a sentence should see it: text without the
 *  trailing blanks a CHAR column is padded with (`'OPEN                '`),
 *  so `{header.status} == 'OPEN'` holds and `**{line.uom}**` stays bold.
 *  Twin of the engine's `templates.unpad`. */
export function unpad(v: unknown): unknown {
  if (typeof v === 'string') return v.replace(/\s+$/, '')
  if (v instanceof Vector) return Vector.of2([...v].map(unpad))
  return v
}

function renderString(s: string, scope: WizardScope, missing: string[]): unknown {
  const whole = /^\s*\{\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_-]+)*)\s*(?:\|([^{}]*))?\}\s*$/.exec(s)
  if (whole) {
    const r = resolvePath(whole[1], scope)
    if (r.known) {
      let v = r.value
      if (v instanceof Vector) v = [...v]
      if (isBlank(v)) {
        if (whole[2] !== undefined) return parseDefault(whole[2])
        missing.push(whole[1])
        return v ?? null
      }
      return v
    }
  }
  return s.replace(TOKEN_RE, (m, path: string, dflt?: string) => {
    const r = resolvePath(path, scope)
    if (!r.known) return m
    let v = unpad(r.value)
    if (v instanceof Vector) v = [...v].join(', ')
    if (isBlank(v)) {
      if (dflt !== undefined) return String(parseDefault(dflt))
      missing.push(path)
      return ''
    }
    return String(v)
  })
}

/** Render a `{name: template}` map, keeping only the entries that resolved
 *  (`skipMissing`) or reporting which did not. */
export function renderMap(
  map: Record<string, unknown> | undefined, scope: WizardScope,
): { values: Record<string, unknown>; missing: string[] } {
  const values: Record<string, unknown> = {}
  const missing: string[] = []
  for (const [k, tpl] of Object.entries(map || {})) {
    const r = renderTemplate(tpl, scope)
    if (r.missing.length) { missing.push(k); continue }
    values[k] = r.value
  }
  return { values, missing }
}

// ── Expressions ──────────────────────────────────────────────────────────

const STRING_LITERAL_RE = /'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g

/** Rewrite wizard tokens into plain identifiers the language can bind —
 *  `{line.qty}` → `line__qty`, `{lines.qty}` → a Vector — leaving string
 *  literals untouched. Returns the source and the bound variables. */
export function bindTokens(expr: string, scope: WizardScope): { src: string; variables: Record<string, unknown> } {
  const variables: Record<string, unknown> = {}
  const src = String(expr ?? '')
  let out = ''
  let last = 0
  const rewrite = (seg: string) => seg.replace(TOKEN_RE, (m, path: string, dflt?: string) => {
    const r = resolvePath(path, scope)
    if (!r.known) return m
    // `line.qty` → `line__qty`; a token with a default gets its own name, as
    // `{scan.30|1}` and `{scan.30}` may both appear and mean different things.
    const name = path.split('.').map(p => p.replace(/[^A-Za-z0-9_]/g, '_')).join('__')
      + (dflt !== undefined ? `__d${Object.keys(variables).length}` : '')
    let v = r.value
    if (isBlank(v) && dflt !== undefined) v = parseDefault(dflt)
    if (v === undefined) v = null
    variables[name] = unpad(v)
    return name
  })
  STRING_LITERAL_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = STRING_LITERAL_RE.exec(src)) !== null) {
    out += rewrite(src.slice(last, m.index)) + m[0]
    last = m.index + m[0].length
  }
  out += rewrite(src.slice(last))
  return { src: out, variables }
}

export const WIZARD_FUNCS = { ...SAFE_FUNCS, ...DATE_FUNCS }

/** Evaluate an expression in the platform language over the wizard's values. */
export function evaluateExpr(expr: string, scope: WizardScope, opts: { allowVector?: boolean } = {}): unknown {
  const { src, variables } = bindTokens(expr, scope)
  return evaluate(src, { variables, functions: WIZARD_FUNCS, allowVector: opts.allowVector })
}

/** A condition. Absent ⇒ `dflt`. An expression that fails to evaluate is
 *  FALSE (and reported once on the console) — a broken `when` must not let a
 *  step through that was meant to be guarded. */
export function evalCondition(expr: string | undefined | null, scope: WizardScope, dflt = true): boolean {
  if (expr === undefined || expr === null || String(expr).trim() === '') return dflt
  try {
    return truthy(evaluateExpr(String(expr), scope))
  } catch (err) {
    if (typeof console !== 'undefined') {
      console.warn(`[wizard] condition "${expr}" failed:`, err instanceof ExprError ? err.message : err)
    }
    return false
  }
}

// ── Routing references ───────────────────────────────────────────────────

export type Route =
  | { goto: string }
  | { finish: true }

/** `"step"` / `"goto:step"` → a route; `"finish"` → finish; anything else null. */
export function routeOf(ref: string | null | undefined): Route | null {
  const s = String(ref ?? '').trim()
  if (!s) return null
  if (s === 'finish' || s === 'goto:finish') return { finish: true }
  if (s.startsWith('goto:')) return { goto: s.slice(5).trim() }
  if (s.includes(':')) return null
  return { goto: s }
}

/** The route a step's `next` (or a branch's `cases` / `else`) picks. */
export function nextRoute(step: WizardStep, scope: WizardScope): Route | null {
  if (step.kind === 'branch' && (step.cases?.length || step.else)) {
    for (const c of step.cases || []) {
      if (evalCondition(c.when, scope, false)) return routeOf(c.goto)
    }
    if (step.else) return routeOf(step.else)
  }
  return resolveNext(step.next, scope)
}

export function resolveNext(next: WizardNext | undefined, scope: WizardScope): Route | null {
  if (next === undefined || next === null) return null
  if (typeof next === 'string') return routeOf(next)
  if (Array.isArray(next)) {
    for (const c of next) {
      const when = (c as { when?: string }).when
      if (when !== undefined && when !== '' && !evalCondition(when, scope, false)) continue
      if ((c as { finish?: boolean }).finish) return { finish: true }
      return routeOf((c as { goto: string }).goto)
    }
    return null
  }
  if (typeof next === 'object' && (next as { finish?: boolean }).finish) return { finish: true }
  return null
}

/** Every step id a step can route to (for the progress estimate). */
export function routeTargets(step: WizardStep): string[] {
  const out: string[] = []
  const add = (r: string | undefined) => { const x = routeOf(r); if (x && 'goto' in x) out.push(x.goto) }
  const n = step.next
  if (typeof n === 'string') add(n)
  else if (Array.isArray(n)) n.forEach(c => add((c as { goto?: string }).goto))
  for (const c of step.cases || []) add(c.goto)
  add(step.else)
  if (step.kind === 'repeat' && step.body) add(step.body)
  add(step.on_saved)
  add(step.on_pending_approval)
  return out
}

// ── Template text for titles ─────────────────────────────────────────────

/** A title / message: always a string. */
export function text(tpl: string | undefined | null, scope: WizardScope): string {
  if (!tpl) return ''
  const v = render(tpl, scope)
  if (v === null || v === undefined) return ''
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

// ── Lookup filters ───────────────────────────────────────────────────────

const FILTER_TOKEN_RE = /\{([A-Za-z_][A-Za-z0-9_]*)((?:\.[A-Za-z0-9_$#]+)*)(?:\|[^{}]*)?\}/g

/** A wizard `source.filter` / lookup-op `filter` as the engine runs it — the
 *  mirror of `wizard.authz._wizard_filter`: `{line.X}` → `{form.X}`,
 *  `{header.X}` kept, every other token → `{lit.wN}` (numbered in order of
 *  appearance) whose VALUE rides `literalValues`, bound as a parameter. The
 *  server re-derives the filter from the MODEL and keeps only the `wN` values
 *  it declares, so the two numberings must agree. */
export function lookupFilter(
  filter: string | undefined | null, scope: WizardScope,
): { filter?: string; literalValues?: Record<string, unknown> } {
  const src = String(filter || '').trim()
  if (!src) return {}
  const literalValues: Record<string, unknown> = {}
  let n = 0
  const out = src.replace(FILTER_TOKEN_RE, (whole, head: string, dotted: string) => {
    const root = head.toLowerCase()
    const path = (dotted || '').slice(1)
    if (root === 'line' && path && !path.includes('.')) return `{form.${path}}`
    if ((root === 'header' || root === 'form') && path && !path.includes('.')) return `{${root}.${path}}`
    const name = `w${n++}`
    literalValues[name] = render(whole, scope)
    return `{lit.${name}}`
  })
  return { filter: out, literalValues }
}
